/**
 * Datums en indeling voor de agenda van de planning.
 *
 * De weergaven zijn die van de agenda in Outlook: Dag, Drie dagen, Werkweek, Week,
 * Maand en Lijst. Een week begint op maandag en heeft een ISO-weeknummer, zoals in
 * Nederland gebruikelijk.
 */
import { leesTijdstip, type Werkuren } from '@houtstaat/engine';

export type Weergave = 'dag' | 'drie' | 'werkweek' | 'week' | 'maand' | 'lijst';

export const WEERGAVEN: Array<{ waarde: Weergave; label: string }> = [
  { waarde: 'dag', label: 'Dag' },
  { waarde: 'drie', label: 'Drie dagen' },
  { waarde: 'werkweek', label: 'Werkweek' },
  { waarde: 'week', label: 'Week' },
  { waarde: 'maand', label: 'Maand' },
  { waarde: 'lijst', label: 'Lijst' },
];

export const isTijdraster = (w: Weergave): boolean => w !== 'maand' && w !== 'lijst';

export const dagBegin = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export const plusDagen = (d: Date, n: number): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n, d.getHours(), d.getMinutes());
export const zelfdeDag = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
export const maandagVan = (d: Date): Date => plusDagen(dagBegin(d), -((d.getDay() + 6) % 7));
export const minuutVanDag = (d: Date): number => d.getHours() * 60 + d.getMinutes();
export const opMinuut = (dag: Date, minuut: number): Date =>
  new Date(dag.getFullYear(), dag.getMonth(), dag.getDate(), 0, minuut);

/** ISO-weeknummer: week 1 is de week met de eerste donderdag van het jaar. */
export function weeknummer(d: Date): number {
  const donderdag = plusDagen(maandagVan(d), 3);
  const eersteJan = new Date(donderdag.getFullYear(), 0, 1);
  // Afronden op hele dagen: tussen januari en de zomertijd zit een uur minder.
  const dagen = Math.round((donderdag.getTime() - eersteJan.getTime()) / 86_400_000);
  return 1 + Math.floor(dagen / 7);
}

/** De zes weken van het maandoverzicht, van de maandag vóór de eerste van de maand. */
export function maandraster(datum: Date): Date[] {
  const eerste = maandagVan(new Date(datum.getFullYear(), datum.getMonth(), 1));
  return Array.from({ length: 42 }, (_, i) => plusDagen(eerste, i));
}

/** De dagen die een weergave rond `datum` laat zien. */
export function dagenVan(weergave: Weergave, datum: Date): Date[] {
  const dag = dagBegin(datum);
  switch (weergave) {
    case 'dag':
      return [dag];
    case 'drie':
      return [dag, plusDagen(dag, 1), plusDagen(dag, 2)];
    case 'werkweek':
      return Array.from({ length: 5 }, (_, i) => plusDagen(maandagVan(dag), i));
    case 'week':
      return Array.from({ length: 7 }, (_, i) => plusDagen(maandagVan(dag), i));
    case 'maand':
      return maandraster(dag);
    case 'lijst': {
      const eerste = new Date(dag.getFullYear(), dag.getMonth(), 1);
      const aantal = new Date(dag.getFullYear(), dag.getMonth() + 1, 0).getDate();
      return Array.from({ length: aantal }, (_, i) => plusDagen(eerste, i));
    }
  }
}

/** Vorige of volgende periode: een dag, drie dagen, een week of een maand. */
export function verschuif(weergave: Weergave, datum: Date, richting: 1 | -1): Date {
  if (weergave === 'maand' || weergave === 'lijst') {
    return new Date(datum.getFullYear(), datum.getMonth() + richting, 1);
  }
  const stap = weergave === 'dag' ? 1 : weergave === 'drie' ? 3 : 7;
  return plusDagen(datum, stap * richting);
}

const maandJaar = new Intl.DateTimeFormat('nl-NL', { month: 'long', year: 'numeric' });
const lang = new Intl.DateTimeFormat('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const dagMaand = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long' });
const dagMaandKort = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short' });
export const weekdagKort = new Intl.DateTimeFormat('nl-NL', { weekday: 'short' });
export const weekdagLang = new Intl.DateTimeFormat('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });

/** De kop boven de agenda: "donderdag 22 oktober 2026", "19 – 23 oktober 2026" of "oktober 2026". */
export function periodetitel(weergave: Weergave, dagen: Date[], datum: Date): string {
  if (weergave === 'maand' || weergave === 'lijst') return maandJaar.format(datum);
  if (weergave === 'dag') return lang.format(dagen[0]!);
  const eerste = dagen[0]!;
  const laatste = dagen[dagen.length - 1]!;
  if (eerste.getMonth() === laatste.getMonth()) {
    return `${eerste.getDate()} – ${dagMaand.format(laatste)} ${laatste.getFullYear()}`;
  }
  return `${dagMaandKort.format(eerste)} – ${dagMaandKort.format(laatste)} ${laatste.getFullYear()}`;
}

const twee = (n: number): string => String(n).padStart(2, '0');
export const klok = (minuut: number): string => `${twee(Math.floor(minuut / 60) % 24)}:${twee(minuut % 60)}`;
export const klokVan = (d: Date): string => klok(minuutVanDag(d));

/** "22 okt 07:00 – 24 okt 12:00", of "07:00 – 16:00" als begin en eind op één dag vallen. */
export function tijdvak(start: Date, eind: Date): string {
  if (zelfdeDag(start, eind)) return `${klokVan(start)} – ${klokVan(eind)}`;
  return `${dagMaandKort.format(start)} ${klokVan(start)} – ${dagMaandKort.format(eind)} ${klokVan(eind)}`;
}

// ── Indeling van afspraken ───────────────────────────────────────────────────

/** Het stuk van een afspraak dat op één dag valt, in minuten na middernacht. */
export interface Segment<T> {
  item: T;
  dag: number;
  van: number;
  tot: number;
  /** Begint de afspraak op deze dag, eindigt hij op deze dag? */
  begint: boolean;
  eindigt: boolean;
  kolom: number;
  kolommen: number;
}

/**
 * Knipt de afspraken op per dag en houdt alleen over wat binnen `bereik` valt (de
 * zichtbare uren). Een kar van maandag 07:00 tot woensdag 12:00 wordt zo drie blokken.
 */
export function segmenten<T extends { start?: string; eind?: string }>(
  items: T[],
  dagen: Date[],
  bereik: [number, number],
  /** Dagen waarop niet gewerkt wordt, krijgen dan geen blok: het weekend bij "alleen werkuren". */
  telt: (dag: Date) => boolean = () => true,
): Segment<T>[] {
  const uit: Segment<T>[] = [];
  for (const item of items) {
    if (!item.start || !item.eind) continue;
    const start = leesTijdstip(item.start);
    const eind = leesTijdstip(item.eind);
    dagen.forEach((dag, i) => {
      if (!telt(dag)) return;
      const dagStart = opMinuut(dag, 0).getTime();
      const dagEind = opMinuut(dag, 24 * 60).getTime();
      if (eind.getTime() <= dagStart || start.getTime() >= dagEind) return;
      const van = Math.max(0, (start.getTime() - dagStart) / 60_000);
      const tot = Math.min(24 * 60, (eind.getTime() - dagStart) / 60_000);
      const zichtbaarVan = Math.max(van, bereik[0]);
      const zichtbaarTot = Math.min(tot, bereik[1]);
      if (zichtbaarTot <= zichtbaarVan) return;
      uit.push({
        item,
        dag: i,
        van: zichtbaarVan,
        tot: zichtbaarTot,
        begint: zelfdeDag(start, dag),
        eindigt: zelfdeDag(eind, dag) || (eind.getTime() === dagEind),
        kolom: 0,
        kolommen: 1,
      });
    });
  }
  return uit;
}

/**
 * Overlappende afspraken naast elkaar, zoals in Outlook: binnen een groep die elkaar
 * raakt krijgt elke afspraak de eerste vrije kolom, en de hele groep deelt de breedte.
 * `sleutel` bepaalt welke segmenten samen één kolom van het raster vormen (een dag,
 * of een dag en een bouwnummer bij "naast elkaar").
 */
export function legIndeling<T>(lijst: Segment<T>[], sleutel: (s: Segment<T>) => string): Segment<T>[] {
  const perKolom = new Map<string, Segment<T>[]>();
  for (const s of lijst) perKolom.set(sleutel(s), [...(perKolom.get(sleutel(s)) ?? []), s]);

  for (const groep of perKolom.values()) {
    groep.sort((a, b) => a.van - b.van || b.tot - a.tot);
    let cluster: Segment<T>[] = [];
    let clusterEind = -1;
    const sluit = () => {
      const kolommen = Math.max(1, ...cluster.map((s) => s.kolom + 1));
      for (const s of cluster) s.kolommen = kolommen;
      cluster = [];
    };
    for (const s of groep) {
      if (s.van >= clusterEind) sluit();
      const bezet = new Set(cluster.filter((c) => c.tot > s.van).map((c) => c.kolom));
      let kolom = 0;
      while (bezet.has(kolom)) kolom++;
      s.kolom = kolom;
      cluster.push(s);
      clusterEind = Math.max(clusterEind, s.tot);
    }
    sluit();
  }
  return lijst;
}

/** Het zichtbare deel van de dag in minuten: de werkuren of de hele dag. */
export function urenbereik(alleenWerkuren: boolean, werkuren: Werkuren): [number, number] {
  if (!alleenWerkuren) return [0, 24 * 60];
  return [Math.floor(werkuren.begin / 60) * 60, Math.ceil(werkuren.eind / 60) * 60];
}
